import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createDropDexAppClient,
  DropDexApiError,
  type DropDexAppClient,
  type DropDexDevicePlatform,
  type DropDexUser,
  type SubscriptionSummary
} from "./index";
import type { ContentKind, FeedItem } from "@dropdex/domain";

export type DropDexAccessState =
  | { status: "loading" }
  | { status: "ready"; user: DropDexUser; billing: SubscriptionSummary }
  | { status: "membership-required"; user: DropDexUser; billing: SubscriptionSummary }
  | { status: "error"; message: string; code?: string; requestId?: string };

export interface UseDropDexAccessOptions {
  apiUrl: string;
  getToken: () => Promise<string | null>;
}

export interface DropDexAccessResult {
  state: DropDexAccessState;
  client: DropDexAppClient;
  retry: () => void;
}

export type DropDexContentState =
  | { status: "loading"; items: FeedItem[] }
  | { status: "ready"; items: FeedItem[] }
  | { status: "error"; items: FeedItem[]; message: string; requestId?: string };

export function useDropDexContent(client: DropDexAppClient, kind: ContentKind) {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<DropDexContentState>({ status: "loading", items: [] });

  useEffect(() => {
    let active = true;
    setState({ status: "loading", items: [] });

    void client.getContent(kind).then((page) => {
      if (active) setState({ status: "ready", items: page.items });
    }).catch((cause: unknown) => {
      if (!active) return;
      if (cause instanceof DropDexApiError) {
        setState({ status: "error", items: [], message: cause.message, requestId: cause.requestId });
        return;
      }
      setState({ status: "error", items: [], message: cause instanceof Error ? cause.message : "Unable to load DropDex content." });
    });

    return () => { active = false; };
  }, [attempt, client, kind]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  return { state, retry };
}

function installationId(storageKey: string) {
  const existing = globalThis.localStorage?.getItem(storageKey);
  if (existing) return existing;
  const created = globalThis.crypto?.randomUUID?.() || `installation-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  globalThis.localStorage?.setItem(storageKey, created);
  return created;
}

export function useDropDexDeviceRegistration(
  client: DropDexAppClient,
  platform: DropDexDevicePlatform,
  appVersion = "0.1.0"
) {
  const [status, setStatus] = useState<"registering" | "registered" | "error">("registering");

  useEffect(() => {
    let active = true;
    const storageKey = `dropdex:${platform}:installation-id`;
    void client.registerDevice({
      installationId: installationId(storageKey),
      platform,
      deviceName: globalThis.navigator?.userAgent?.slice(0, 160) || `${platform} device`,
      appVersion
    }).then(() => {
      if (active) setStatus("registered");
    }).catch(() => {
      if (active) setStatus("error");
    });
    return () => { active = false; };
  }, [appVersion, client, platform]);

  return status;
}

export function useDropDexAccess(options: UseDropDexAccessOptions): DropDexAccessResult {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<DropDexAccessState>({ status: "loading" });
  const client = useMemo(() => createDropDexAppClient(options), [options.apiUrl, options.getToken]);

  useEffect(() => {
    let active = true;
    setState({ status: "loading" });

    async function verifyAccess() {
      try {
        const user = await client.getCurrentUser();
        const billing = await client.getSubscription();
        if (!active) return;
        setState(billing.effectiveAccess
          ? { status: "ready", user, billing }
          : { status: "membership-required", user, billing });
      } catch (cause) {
        if (!active) return;
        if (cause instanceof DropDexApiError) {
          setState({
            status: "error",
            message: cause.message,
            code: cause.code,
            requestId: cause.requestId
          });
          return;
        }
        setState({ status: "error", message: cause instanceof Error ? cause.message : "Unable to connect to DropDex." });
      }
    }

    void verifyAccess();
    return () => { active = false; };
  }, [attempt, client]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  return { state, client, retry };
}
